// src/pages/Home/HomePageVitrine.jsx

import React, { useEffect, useMemo, useState } from "react";
import useScrollToTop from "../../hooks/useScollToTop";
import { useUser } from "./UserContext";
import { saveVisite } from "../../services/visitesServices";
import { getAllHomeSections } from "../../services/homeSectionsServices";

import ScrollIndicator from "./ScrollIndicator";
import HeaderSlider from "./HeaderSlider";
import InfoClub from "./InfoClub";
import About from "./About";
import Diaporama from "./Diaporama";
import Disciplines from "./Disciplines/Disciplines";
import GroupTable from "./GroupTable";
import NosEquipes from "./NosEquipes";
import HeaderCount from "./HeaderCount";
import UpcomingEvents from "./UpcomingEvents";
import LastNews from "./LastNews";
import LastPalmares from "./LastPalmares";
import LastGallerie from "./LastGallerie";
import BeltCounts from "./BeltCounts";
import PartenairesCarousel from "./PartenairesCarousel";
import FormContact from "./FormContact";
import HomeUserShortcuts from "./HomeUserShortcuts";
import useHomeShortcutCounters from "../../hooks/useHomeShortcutCounters";

import Reveal from "../../components/Reveal/Reveal";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

const SECTION_ID_BY_SLUG = {
  header: "accueil",
  infoclub: "infoclub",
  about: "about",
  carrousel: "diaporama",
  disciplines: "disciplines",
  infogroupes: "cours",
  planning: "cours",
  team: "equipes",
  stats: "stats",
  events: "dates",
  news: "actualites",
  palmares: "palmares",
  albums: "galerie",
  belt: "grades",
  partenaires: "partenaires",
  contact: "contact",
  photo01: "photo01",
  photo02: "photo02",
  video01: "video01",
};

const OWN_REVEAL_SLUGS = [
  "infoclub",
  "about",
  "carrousel",
  "disciplines",
  "infogroupes",
  "team",
  "stats",
  "events",
  "news",
  "palmares",
  "albums",
  "belt",
  "partenaires",
  "contact",
];

const MEDIA_SLUGS = ["photo01", "photo02", "video01"];

const isExternalURL = (url = "") => {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const getMediaUrl = (url = "") => {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (!cleanUrl) return "";

  if (
    isExternalURL(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const cleanBaseUrl = BASE_URL.replace(/\/$/, "");

  if (cleanUrl.startsWith("/")) {
    return `${cleanBaseUrl}${cleanUrl}`;
  }

  return `${cleanBaseUrl}/${cleanUrl}`;
};

const isImageUrl = (url = "") =>
  /\.(png|jpe?g|webp|gif|avif|bmp|svg)(\?.*)?$/i.test(url) ||
  url.includes("res.cloudinary.com");

const getYouTubeEmbedSrc = (url = "") => {
  if (!url) return null;

  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      const id = u.pathname.slice(1);
      if (id) return `https://www.youtube.com/embed/${id}`;
    }

    if (host.endsWith("youtube.com")) {
      if (u.pathname.startsWith("/embed/")) {
        return `https://www.youtube.com${u.pathname}${u.search}`;
      }

      if (u.pathname === "/watch") {
        const id = u.searchParams.get("v");

        if (id) {
          const start = u.searchParams.get("t") || u.searchParams.get("start");
          const params = new URLSearchParams();

          if (start) {
            const seconds = parseInt(start, 10);
            if (Number.isFinite(seconds)) {
              params.set("start", seconds);
            }
          }

          const query = params.toString();

          return `https://www.youtube.com/embed/${id}${
            query ? `?${query}` : ""
          }`;
        }
      }

      if (u.pathname.startsWith("/shorts/")) {
        const id = u.pathname.split("/")[2];
        if (id) return `https://www.youtube.com/embed/${id}`;
      }

      if (u.pathname.startsWith("/live/")) {
        const id = u.pathname.split("/")[2];
        if (id) return `https://www.youtube.com/embed/${id}`;
      }
    }

    if (url.includes("/embed/")) return url;

    return null;
  } catch {
    return null;
  }
};

const PhotoFull = ({ mediaURL, description, alt = "Photo" }) => {
  const src = getMediaUrl(mediaURL);

  if (!src || !isImageUrl(src)) return null;

  return (
    <figure className="w-full bg-slate-50 py-8 dark:bg-slate-950">
      <img
        src={src}
        alt={description || alt}
        loading="lazy"
        className="mx-auto block h-auto w-full max-w-7xl select-none"
      />

      {description && (
        <figcaption className="mx-auto mt-3 max-w-4xl px-4 text-center text-sm text-slate-500 dark:text-slate-400">
          {description}
        </figcaption>
      )}
    </figure>
  );
};

const VideoEmbed = ({ mediaURL, title = "Vidéo" }) => {
  const src = getYouTubeEmbedSrc(mediaURL || "");

  if (!src) return null;

  return (
    <div className="bg-slate-50 px-4 py-10 dark:bg-slate-950">
      <div className="mx-auto aspect-video w-full max-w-7xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-soft dark:border-white/10 dark:bg-slate-900">
        <iframe
          className="h-full w-full"
          src={src}
          title={title}
          frameBorder="0"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    </div>
  );
};

function HomePageVitrine() {
  useScrollToTop();

  const { user } = useUser();
  const {
    counters: shortcutCounters,
    countsByLabel: shortcutCountsByLabel,
    loading: shortcutCountersLoading,
    errorMsg: shortcutCountersErrorMsg,
    refresh: refreshShortcutCounters,
  } = useHomeShortcutCounters({
    enabled: Boolean(user),
    refreshOnFocus: true,
  });
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    const lastVisitDate = localStorage.getItem("lastVisitDate");

    if (lastVisitDate !== today) {
      saveVisite();
      localStorage.setItem("lastVisitDate", today);
    }
  }, []);

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const data = await getAllHomeSections(true);

        const sorted = Array.isArray(data)
          ? [...data].sort((a, b) => a.position - b.position)
          : [];

        setSections(sorted);
      } catch (error) {
        console.error("Erreur lors du chargement des sections :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSections();
  }, []);

  const registry = useMemo(
    () => ({
      header: () => <HeaderSlider user={user} />,
      infoclub: () => <InfoClub />,
      about: () => <About />,
      carrousel: () => <Diaporama />,
      disciplines: () => <Disciplines />,
      infogroupes: () => <GroupTable />,

      // La vue planning est maintenant gérée dans GroupTable.
      planning: () => null,

      team: () => <NosEquipes />,
      stats: () => <HeaderCount />,
      events: () => <UpcomingEvents />,
      news: () => <LastNews />,
      palmares: () => <LastPalmares />,
      albums: () => <LastGallerie />,
      belt: () => <BeltCounts />,
      partenaires: () => <PartenairesCarousel />,
      contact: () => <FormContact />,

      photo01: (section) => (
        <PhotoFull
          mediaURL={section.mediaURL}
          description={section.description}
          alt="Photo 01"
        />
      ),

      photo02: (section) => (
        <PhotoFull
          mediaURL={section.mediaURL}
          description={section.description}
          alt="Photo 02"
        />
      ),

      video01: (section) => (
        <VideoEmbed
          mediaURL={section.mediaURL}
          title={section.section_name || "Vidéo 01"}
        />
      ),
    }),
    [user],
  );

  const hasHeaderSection = sections.some(
    (section) => section.slug === "header",
  );

  return (
    <div className="min-h-screen overflow-hidden bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <main className="flex-grow">
        <ScrollIndicator />

        {loading && (
          <div className="pt-[var(--site-navbar-h)]">
            <div className="mx-auto max-w-7xl space-y-4 px-4 py-8">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="h-40 animate-pulse rounded-[2rem] bg-slate-100 dark:bg-slate-800"
                />
              ))}
            </div>
          </div>
        )}

        {!loading && !hasHeaderSection && (
          <div style={{ height: "var(--site-navbar-h)" }} aria-hidden />
        )}

        {!loading && !hasHeaderSection && user && (
          <HomeUserShortcuts
            user={user}
            countsByLabel={shortcutCountsByLabel}
          />
        )}

        {!loading &&
          sections.map((section, index) => {
            const render = registry[section.slug];

            if (!render) return null;

            const content = render(section);

            if (!content) return null;

            const sectionId = SECTION_ID_BY_SLUG[section.slug] || section.slug;
            const isHeader = section.slug === "header";
            const isMedia = MEDIA_SLUGS.includes(section.slug);
            const hasOwnReveal = OWN_REVEAL_SLUGS.includes(section.slug);

            if (isHeader) {
              return (
                <React.Fragment key={section.id || section.slug}>
                  <section id={sectionId} className="scroll-mt-0">
                    {content}
                  </section>

                  {user && (
                    <HomeUserShortcuts
                      user={user}
                      countsByLabel={shortcutCountsByLabel}
                      counters={shortcutCounters}
                      countersLoading={shortcutCountersLoading}
                      countersErrorMsg={shortcutCountersErrorMsg}
                      onRefreshCounters={refreshShortcutCounters}
                    />
                  )}
                </React.Fragment>
              );
            }

            return (
              <section
                id={sectionId}
                key={section.id || section.slug}
                className="scroll-mt-[calc(var(--site-navbar-h)+24px)]"
              >
                {isMedia || hasOwnReveal ? (
                  content
                ) : (
                  <Reveal
                    variant={index % 2 === 0 ? "left" : "right"}
                    delay={80}
                  >
                    {content}
                  </Reveal>
                )}
              </section>
            );
          })}
      </main>
    </div>
  );
}

export default HomePageVitrine;
